import React, { useRef, useCallback } from 'react';
import { isEmpty, isFunction } from 'lodash';
import classNames from 'classnames';
import { string, func, bool } from 'prop-types';
import { Button } from '@fiverr-private/button';
import { PaperPlaneIcon } from '@fiverr-private/icons';
import { white, grey_800 } from '@fiverr-private/sass/helpers';
import { translate } from '@fiverr-private/i18n-react';
import { selectSendMessageCtaTitle, useStaticContext } from '../../../context/contexts/static';
import {
    selectErrorFlag,
    selectSendingFlag,
    selectSubmitHandler,
    useValidationContext
} from '../../../context/contexts/validation';
import { useMessage } from '../../../context/contexts/message';
import { CLICKED_ON_SEND_MESSAGE } from '../../../constants/biEvents';
import { useReporting } from '../../../context/contexts/reporting';
import { useAttachments } from '../../../context/contexts/attachments';
import { useBatchBrief } from '../../../context/contexts/sendBatchBrief';

import classes from './style.module.scss';

const SubmitButton = ({
    copyKeyPrefix,
    drawerView,
    onSubmitSuccess,
    onSubmitFailure,
    onSubmit
}) => {
    const context = useStaticContext();
    const validationContext = useValidationContext();
    const { message } = useMessage();
    const { isBatchBrief } = useBatchBrief();
    const { sendBiEvent, sendMixpanelEvent } = useReporting();
    const { attachments } = useAttachments();
    const { errors } = selectErrorFlag(validationContext);
    const sendMessageCtaTitle = selectSendMessageCtaTitle(context);
    const { isSending } = selectSendingFlag(validationContext);
    const submitHandler = selectSubmitHandler(validationContext);

    const showIcon = !isSending;
    const buttonLabelKey = isSending ? 'sending' : 'send_message';
    const disabled = !isEmpty(errors);

    const submitPayload = useRef({});
    submitPayload.current = { message, attachments };

    const handleSubmit = useCallback(
        () => {
            if (isFunction(onSubmit)) {
                return onSubmit();
            }

            const { message, attachments = [] } = submitPayload.current;
            const event = {
                type: CLICKED_ON_SEND_MESSAGE,
                params: {
                    message,
                    elementName: drawerView ? 'first_message_drawer' : '',
                    attachmentsCount: attachments.length
                }
            };

            sendBiEvent(event);
            sendMixpanelEvent(event);
            submitHandler({ message, attachments, onSubmitSuccess, onSubmitFailure, sendBatchBrief: isBatchBrief });
        },
        [sendMixpanelEvent, sendBiEvent, submitHandler, onSubmitSuccess, onSubmitFailure, onSubmit, drawerView, isBatchBrief]
    );

    const wrapperClasses = classNames(
        classes.submitButton,
        { [classes.disabled]: disabled, [classes.drawerView]: drawerView }
    );
    const iconColor = drawerView && disabled ? grey_800 : white;

    const defaultTitle = translate(`${copyKeyPrefix}.button.${buttonLabelKey}`);
    const title = sendMessageCtaTitle || defaultTitle;

    return (
        <Button disabled={disabled}
            onClick={handleSubmit}
            className={wrapperClasses}>
            {showIcon && (
                <PaperPlaneIcon color={iconColor} data-testid="send-icon"/>
            )}
            <p>
                {title}
            </p>
        </Button>
    );
};

SubmitButton.propTypes = {
    copyKeyPrefix: string.isRequired,
    onSubmitSuccess: func.isRequired,
    onSubmitFailure: func.isRequired,
    drawerView: bool,
    onSubmit: func
};

export default SubmitButton;
